import { useQuery } from '@tanstack/react-query';
import { BookOpen, Image, Megaphone, Mic2, Video } from 'lucide-react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';

import { Card } from '@/components/ui/card';
import { IconWell } from '@/components/ui/icon-well';
import { Skeleton } from '@/components/ui/skeleton';
import { api } from '@/lib/api';
import { ContentPageHeader } from '@/pages/content/content-page-header';
import { useContentPeriodStore } from '@/pages/content/content-period-store';
import { cn } from '@/utils/cn';
import { formatNumber } from '@/utils/format';

interface ContentOverviewInsights {
  sermons: { published: number; unpublished: number };
  devotionals: { published: number; unpublished: number };
  announcements: { published: number; unpublished: number };
  videos: { published: number; unpublished: number };
  galleries: { published: number; unpublished: number };
}

type MetricTile = {
  to: string;
  label: string;
  value: number | undefined;
  caption: string | undefined;
  icon: ReactNode;
};

export const ContentHomePage = () => {
  const { t } = useTranslation('content');
  const dateRange = useContentPeriodStore((state) => state.dateRange);
  const dateFrom = dateRange.from;
  const dateTo = dateRange.to;

  const insights = useQuery({
    queryKey: ['content', 'insights', dateFrom, dateTo],
    queryFn: () =>
      api.get<ContentOverviewInsights>('/content/insights', {
        query: {
          dateFrom: dateFrom ?? undefined,
          dateTo: dateTo ?? undefined,
        },
      }),
  });

  const data = insights.data;
  const loading = insights.isLoading;

  const tiles: MetricTile[] = [
    {
      to: '/content/sermons',
      label: t('nav.sermons'),
      value: data?.sermons.published,
      caption: data
        ? t('overview.unpublishedCount', { count: data.sermons.unpublished })
        : undefined,
      icon: <Mic2 className="size-5" aria-hidden />,
    },
    {
      to: '/content/devotionals',
      label: t('nav.devotionals'),
      value: data?.devotionals.published,
      caption: data
        ? t('overview.unpublishedCount', { count: data.devotionals.unpublished })
        : undefined,
      icon: <BookOpen className="size-5" aria-hidden />,
    },
    {
      to: '/content/announcements',
      label: t('nav.announcements'),
      value: data?.announcements.published,
      caption: data
        ? t('overview.unpublishedCount', { count: data.announcements.unpublished })
        : undefined,
      icon: <Megaphone className="size-5" aria-hidden />,
    },
    {
      to: '/content/videos',
      label: t('nav.videos'),
      value: data?.videos.published,
      caption: data
        ? t('overview.unpublishedCount', { count: data.videos.unpublished })
        : undefined,
      icon: <Video className="size-5" aria-hidden />,
    },
    {
      to: '/content/galleries',
      label: t('nav.galleries'),
      value: data?.galleries.published,
      caption: data
        ? t('overview.unpublishedCount', { count: data.galleries.unpublished })
        : undefined,
      icon: <Image className="size-5" aria-hidden />,
    },
  ];

  return (
    <div>
      <ContentPageHeader />
      <p className="mb-4 text-body-sm text-fg-secondary">{t('overview.title')}</p>
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {tiles.map((tile) => (
          <Link
            key={tile.to}
            to={tile.to}
            className="block rounded-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand"
          >
            <Card
              className={cn(
                'h-full p-4 transition-colors hover:border-brand/40 hover:bg-surface-sunken/40',
              )}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-body-sm text-fg-secondary">{tile.label}</p>
                  <div className="mt-1.5">
                    {loading || tile.value === undefined ? (
                      <Skeleton className="h-8 w-20" />
                    ) : (
                      <p className="text-display font-semibold tabular-nums text-fg">
                        {formatNumber(tile.value)}
                      </p>
                    )}
                  </div>
                  <div className="mt-2">
                    {loading || tile.caption === undefined ? (
                      <Skeleton className="h-4 w-28" />
                    ) : (
                      <p className="text-caption text-fg-tertiary">{tile.caption}</p>
                    )}
                  </div>
                </div>
                <IconWell>{tile.icon}</IconWell>
              </div>
            </Card>
          </Link>
        ))}
      </div>
    </div>
  );
};
